import { model } from '@gosupersimple/types';

import { isString } from 'lodash';

import { formatInTimeZone } from 'date-fns-tz';

import { createBasePipeline, createPipeline, isBasePipeline } from '@/core/pipeline';
import { generateCellId } from '@/core/cell';

import { Json } from '@/lib/types';
import { notNil } from '@/lib/utils';
import { getDateRangeFromPrecision, getISOFormatForPrecision } from '@/lib/date';

import { getPipelineById } from '../exploration/utils';
import {
  Aggregation,
  DereferencedPipeline,
  DereferencedPipelineOperation,
  Exploration,
  Field,
  FilterOperation,
  GroupAggregateOperation,
  Pipeline,
  PipelineOperation,
  RecordsCell,
  RelationAggregateOperation,
  SortItem,
  TimeAggregationPeriod,
} from '../types';
import { flattenPipeline } from '../pipeline/utils';
import { parseValueForFilter } from '../edit-pipeline/utils/filter';
import {
  isAggregatingOperation,
  isFilteringAggregation,
  isGroupAggregateOperation,
  isRelationAggregateOperation,
} from '../pipeline/operation';

type Property = {
  key: string;
  pk?: boolean;
  type: model.PropertyType | null;
  precision?: TimeAggregationPeriod;
};

type DrilldownAwareProperty<T extends Property> = T & { isAggregated: boolean };

export type GroupFilter = {
  key: string;
  value: Json;
  precision?: TimeAggregationPeriod;
};

const extractAggregationKeys = (aggregations: Aggregation[]) =>
  aggregations
    .filter((aggregation) => aggregation.type !== 'custom')
    .map((aggregation) => aggregation.property.key);

const getAggregatedPropertiesFromOperation = (operation: DereferencedPipelineOperation) =>
  operation.operation === 'groupAggregate' || operation.operation === 'relationAggregate'
    ? extractAggregationKeys(operation.parameters.aggregations)
    : [];

const getAggregatedPropertiesFromPipeline = (pipeline: DereferencedPipeline) => {
  return new Set(pipeline.operations.flatMap(getAggregatedPropertiesFromOperation));
};

export const detectAggregatedProperties = <T extends Property>(
  properties: T[],
  pipeline: DereferencedPipeline,
): DrilldownAwareProperty<T>[] => {
  const aggregatedProperties = getAggregatedPropertiesFromPipeline(pipeline);
  return properties.map((property) => ({
    isAggregated: aggregatedProperties.has(property.key),
    ...property,
  }));
};

/**
 * Given the aggregation field key of a property, figure out which operation in the pipeline introduces the aggregation.
 */
export function findOperationIntroducingAggregatingProperty(
  pipeline: Pipeline,
  key: string,
): GroupAggregateOperation | RelationAggregateOperation | undefined {
  const operation = pipeline.operations
    .filter(isAggregatingOperation)
    .findLast((operation) =>
      operation.parameters.aggregations.some((aggregation) => aggregation.property.key === key),
    );

  if (operation === undefined) {
    return;
  }

  return operation;
}

/**
 * Given aggregating operation, return its "reverse" operations that builds the query to drill down to the origins of
 * that aggregation.
 */
export function drillDownFromAggregatingOperation(input: {
  key: string;
  filterOperations: FilterOperation[];
  pipeline: Pipeline;
  exploration: Exploration;
}): { pipeline: Pipeline; sort?: SortItem } {
  const { key, filterOperations, pipeline, exploration } = input;

  const flattenedPipeline = flattenPipeline(pipeline, exploration);

  const operation =
    findOperationIntroducingAggregatingProperty(flattenedPipeline, key) ??
    flattenedPipeline.operations.findLast(isAggregatingOperation);

  if (operation === undefined) {
    throw new Error(`No aggregating operation found for key ${key}`);
  }

  const slicedPipeline = createPipeline({
    ...pipeline,
    operations: pipeline.operations.slice(0, pipeline.operations.indexOf(operation)),
  });

  if (isGroupAggregateOperation(operation)) {
    return drilldownFromGroupAggregate(key, operation, filterOperations, slicedPipeline);
  }

  if (isRelationAggregateOperation(operation)) {
    return drillDownFromRelationAggregate(
      key,
      operation,
      filterOperations,
      exploration,
      slicedPipeline,
    );
  }

  throw new Error(`Expected groupAggregate or relationAggregate operation`);
}

export const buildDateFilterFromPrecision = (input: {
  date: Date;
  precision: Exclude<TimeAggregationPeriod, 'day_of_week'>;
  key: string;
  timezone: string;
}): FilterOperation => {
  const { date, precision, key, timezone } = input;
  const { startDate, endDate } = getDateRangeFromPrecision(date, precision, timezone);

  return {
    operation: 'filter',
    parameters: {
      key,
      operator: '==',
      value: {
        from: formatInTimeZone(
          startDate.toISOString(),
          timezone,
          getISOFormatForPrecision(precision),
        ),
        to: formatInTimeZone(endDate.toISOString(), timezone, getISOFormatForPrecision(precision)),
      },
    },
  };
};

export const buildFilterFromGroupFilter = (input: {
  groupFilter: { key: string; value: Json; precision?: TimeAggregationPeriod };
  field: Field;
  timezone: string;
}): FilterOperation => {
  const { groupFilter, field, timezone } = input;

  if (groupFilter.value === null) {
    return {
      operation: 'filter' as const,
      parameters: {
        key: groupFilter.key,
        operator: 'isnull' as const,
      },
    };
  }

  if (groupFilter.precision !== undefined) {
    if (groupFilter.precision === 'day_of_week') {
      throw new Error(`Drilldown on day_of_week precision is not supported.`);
    }

    if (!isString(groupFilter.value)) {
      throw new Error(
        `Drilldown on ${groupFilter.precision} precision requires a string value, but got ${typeof groupFilter.value}`,
      );
    }

    return buildDateFilterFromPrecision({
      date: new Date(groupFilter.value),
      precision: groupFilter.precision,
      key: groupFilter.key,
      timezone,
    });
  }

  if (groupFilter.key !== field.key) {
    return {
      operation: 'filter' as const,
      parameters: {
        key: groupFilter.key,
        operator: '==' as const,
        value: parseValueForFilter(groupFilter.value, null, timezone),
      },
    };
  }

  return {
    operation: 'filter' as const,
    parameters: {
      key: groupFilter.key,
      operator: '==' as const,
      value: parseValueForFilter(groupFilter.value, field.type, timezone),
    },
  };
};

const drilldownFromGroupAggregate = (
  key: string,
  operation: GroupAggregateOperation,
  filterOperations: FilterOperation[],
  startingPipeline: Pipeline,
): { pipeline: Pipeline; sort?: SortItem } => {
  const filtersFromAggregations = operation.parameters.aggregations
    .filter((aggregation) => aggregation.property.key === key)
    .filter(isFilteringAggregation)
    .map((agg) => agg.filter)
    .filter(notNil)
    .map<PipelineOperation>((agg) => ({ operation: 'filter', parameters: agg }));

  const sort = operation.parameters.aggregations
    .filter((agg) => key === agg.property.key)
    .map(buildSortFromAggregation)
    .find(notNil);

  const aggregatingOperations: PipelineOperation[] = operation.parameters.aggregations
    .filter((agg) => key === agg.property.key)
    .map<PipelineOperation | undefined>((agg) => {
      if (agg.type === 'count_distinct') {
        return {
          operation: 'groupAggregate',
          parameters: {
            aggregations: [{ type: 'count', property: { key: agg.property.key, name: 'Count' } }],
            groups: [{ key: agg.key }],
          },
        };
      }

      if (agg.type === 'sum') {
        return {
          operation: 'filter',
          parameters: { key: agg.key, operator: '!=', value: 0 },
        };
      }
    })
    .filter(notNil);

  return {
    pipeline: createPipeline({
      ...startingPipeline,
      operations: [
        ...startingPipeline.operations,
        ...filtersFromAggregations,
        ...filterOperations,
        ...aggregatingOperations,
      ],
    }),
    sort,
  };
};

function drillDownFromRelationAggregate(
  key: string,
  operation: RelationAggregateOperation,
  filterOperations: FilterOperation[],
  exploration: Exploration,
  startingPipeline: Pipeline,
): { pipeline: Pipeline; sort?: SortItem } {
  const relatedPipelineId = operation.parameters.pipelineId;
  const relatedPipelineOperations =
    relatedPipelineId !== undefined
      ? getPipelineById(relatedPipelineId, exploration).operations
      : [];

  const filtersFromParameters: PipelineOperation[] =
    operation.parameters.filter !== undefined
      ? [{ operation: 'filter', parameters: operation.parameters.filter }]
      : [];

  const sort = operation.parameters.aggregations
    .filter((agg) => key === agg.property.key)
    .map(buildSortFromAggregation)
    .find(notNil);

  if ('relation' in operation.parameters) {
    const filtersFromAggregations = operation.parameters.aggregations
      .filter((aggregation) => aggregation.property.key === key)
      .filter(isFilteringAggregation)
      .map((agg) => agg.filter)
      .filter(notNil)
      .map<PipelineOperation>((agg) => ({ operation: 'filter', parameters: agg }));

    const operations: PipelineOperation[] = [
      ...filterOperations,
      { operation: 'switchToRelation', parameters: { relation: operation.parameters.relation } },
      ...relatedPipelineOperations,
      ...filtersFromParameters,
      ...filtersFromAggregations,
    ];

    return {
      pipeline: {
        ...startingPipeline,
        operations: [...startingPipeline.operations, ...operations],
      },
      sort,
    };
  } else if ('pipelineId' in operation.parameters) {
    const operations: PipelineOperation[] = [...filtersFromParameters, ...filterOperations];

    const parentPipeline = getPipelineById(operation.parameters.pipelineId, exploration);
    if (!isBasePipeline(parentPipeline)) {
      return { pipeline: parentPipeline, sort };
    }

    return {
      pipeline: createBasePipeline({
        baseModelId: parentPipeline.baseModelId,
        operations: [...parentPipeline.operations, ...operations],
      }),
      sort,
    };
  }

  return { pipeline: startingPipeline, sort };
}

const buildSortFromAggregation = (agg: Aggregation): SortItem | undefined => {
  if (agg.type === 'min') {
    return { key: agg.key, direction: 'ASC' as const };
  }
  if (agg.type === 'max') {
    return { key: agg.key, direction: 'DESC' as const };
  }
  return undefined;
};

export const createDrilldownCell = (
  pipeline: Pipeline,
  fromTitle?: string,
  sort?: SortItem,
): RecordsCell => ({
  id: generateCellId(),
  kind: 'records',
  title: fromTitle !== undefined ? 'Drilldown from ' + fromTitle : 'Drilldown',
  pipeline: createPipeline(pipeline),
  sort: sort !== undefined ? [sort] : undefined,
});
